import { HttpAgent, InputContent, Message, RunErrorEvent } from '@ag-ui/client';
import { AgentHandoff, AgentToolSpec } from '@blisspointmedia/bpm-types/dist/agents';
import { QueryConfig } from '@blisspointmedia/bpm-types/dist/querying';
import { datadogRum } from '@datadog/browser-rum';
import {
  AGENT_HANDOFF_MIN_REMAINING_MS,
  AGENT_STALE_MIN_MS,
  getTokens,
  tokenLifetimeMs
} from './auth/auth-client';

export interface SendPromptToAgentArgs {
  agentEndpoint: string;
  sessionId: string;
  prompt: string;
  /**
   * Fired every time a new segment is captured (text upserted, tool call ended,
   * snapshot processed). The handler is passed a fresh array of the current
   * segments so consumers can replace assistant-message state and stream live.
   */
  onUpdate?: (segments: AgentResponseSegment[]) => void;
  /**
   * Scopes the agent to a specific company. Sent in the AG-UI run's `forwardedProps` (which reaches
   * the runtime — the dashboard agent reads it there) AND as the legacy
   * X-Amzn-...-Runtime-Custom-Client-Slug header. AgentCore strips the custom header at its boundary,
   * so the header is effectively a no-op, but it's kept so other agents' requests stay unchanged.
   */
  company?: string;
  /**
   * Model-run KPI selected on the host page (e.g. the DTC MMM dropdown). Delivered in the AG-UI run's
   * `forwardedProps` (the primary channel — it reaches the runtime, so the agent loads the matching run
   * from S3) AND, for legacy/CLI callers, as the X-Amzn-Bedrock-AgentCore-Runtime-Custom-Kpi header.
   * AgentCore strips that custom header at its boundary, so the header path is effectively a no-op.
   * The MMM Agent falls back to S3-discovered defaults when omitted, so non-MMM hosts can skip it.
   */
  kpi?: string;
  /**
   * Model-run snapshot timestamp selected on the host page. Delivered in `forwardedProps` (primary,
   * survives the AgentCore boundary) AND as the stripped legacy X-Amzn-Bedrock-AgentCore-Runtime-Custom-Timestamp
   * header. Pair with `kpi` — sending one without the other reverts the agent to its default-resolution path.
   */
  timestamp?: string;
  /**
   * Which MMM product the host page is (e.g. 'dtc' or 'commerce' — MmmDashboardConfig.modelType).
   * Delivered in `forwardedProps` only. DTC and Commerce runs share one S3 layout, so the MMM Agent
   * can't tell them apart from the run itself; without this it calls the model "the MMM" rather
   * than guessing which product the user is on.
   */
  modelType?: string;
  /**
   * Optional grounding context folded into this run's user message (before the prompt, with a
   * delimiter). Hosts use it to ground the agent once per session — e.g. the dashboard agent
   * injects the ChartConfig shape rules + valid-id catalog. We fold it into the user turn rather
   * than send a `system` message because the AgentCore-hosted agent does not forward inbound
   * system messages to the model (verified: a system-injected shape guide had zero effect).
   */
  sessionContext?: string;
  /**
   * Optional per-turn context folded into EVERY run's user message (after `sessionContext`, before
   * the prompt). Unlike `sessionContext` (sent once per session), this is re-sent each turn because
   * it can change between turns — the dashboard agent uses it to carry the artifact currently in
   * focus (the chart/dashboard the user is viewing) so a refinement edits that artifact in place.
   */
  focusContext?: string;
  /**
   * Fired for each AG-UI CUSTOM event the agent emits mid-run, forwarded verbatim by name/value.
   * The transport is domain-agnostic: it surfaces every custom event and lets the host interpret
   * the ones it cares about (e.g. the Elevate demo reads `governance.audit` / `governance.policy`
   * to build its audit trail). Hosts that don't pass a handler simply ignore custom events.
   */
  onCustomEvent?: (event: AgentCustomEvent) => void;
  /**
   * When aborted, fires a separate cooperative-cancel request on the same session/thread
   * (`forwardedProps: { mode: 'cancel', run_id }`) rather than touching this run's own
   * connection — dropping the connection doesn't stop the agent. The original run keeps
   * streaming; the backend injects a real "Cancelled by user." message into it once the agent
   * notices the flag. Only agents in `AGENTS_SUPPORTING_CANCEL` implement this; others would treat
   * the cancel request as a normal run.
   */
  signal?: AbortSignal;
  /**
   * AG-UI frontend tools offered to the agent for this run (the combined agent chat's `handoff`).
   * Omitted everywhere else, so those runs send no `tools` key.
   */
  tools?: AgentToolSpec[];
  /** The handoff this run continues, delivered in `forwardedProps.handoff` (first run after one). */
  handoff?: AgentHandoff;
  /**
   * A file sent with this run's user message, as an AG-UI `document` part beside the text. Only hosts
   * whose agent accepts attachments set it; every other run keeps a plain-string message.
   */
  attachment?: AgentAttachment;
}

// A file attached to the user's message. `data` is the file's bytes, base64-encoded.
export interface AgentAttachment {
  filename: string;
  mimeType: string;
  data: string;
}

// A raw AG-UI CUSTOM event surfaced to the host: the event name plus its opaque value. Keeping this
// generic (rather than, say, a governance-specific callback) keeps app concepts out of the shared
// agent API — the host narrows `value` for the names it understands.
export interface AgentCustomEvent {
  name: string;
  value: unknown;
}

export interface AgentTextSegment {
  type: 'text';
  content: string;
}

export interface AgentToolCallSegment {
  type: 'tool_call';
  name: string;
  args: Record<string, unknown>;
  // Human-readable name the agent publishes for the tool (its MCP `title`). Absent when the tool has
  // none; fall back to a prettified `name`.
  title?: string;
  // The QueryConfig a data-query tool reports having run (`executed_config` in its result). Set when
  // the result event arrives; absent for other tools or a result without one.
  executedConfig?: QueryConfig;
  // The `result_id` of a successful data-query result: the name a handoff uses to reference it.
  resultId?: string;
  // Whether the tool's result has arrived, and if so whether it succeeded. Absent until it arrives.
  resultOk?: boolean;
  // Set when the result explicitly rejects the call (see toolResultRejected).
  resultRejected?: boolean;
}

// Emitted when the agent has uploaded a file to S3 and embedded a download
// marker in its response text. The URL must be a pre-signed S3 URL generated
// with ResponseContentDisposition=attachment so the browser downloads rather
// than navigates.
// url is absent when the segment was loaded from persisted history — pre-signed
// S3 URLs expire, so they are stripped before storage and the download button
// renders an expired indicator instead.
export interface AgentArtifactSegment {
  type: 'artifact';
  filename: string;
  url?: string;
}

// A client-side system note on a message (e.g. "Handed to Data Analysis"). Never emitted by an agent;
// the host adds it and it's persisted with the message so History shows it too.
export interface AgentNoteSegment {
  type: 'note';
  content: string;
}

export type AgentResponseSegment =
  AgentTextSegment | AgentToolCallSegment | AgentArtifactSegment | AgentNoteSegment;

export interface AgentResponse {
  /** All text segments joined with double newlines — for non-internal rendering. */
  text: string;
  /** Ordered list of every text and tool-call segment the agent emitted, in run order. */
  segments: AgentResponseSegment[];
  /** True when this run ended early via a cooperative cancel request (see `signal` above). */
  cancelled: boolean;
}

const HANDOFF_FLOOR_MARGIN_MS = 5 * 60 * 1000;

let reportedShortLifetime = false;

// Agent hand-offs target 8 h access tokens; a shorter configured lifetime is reported once per page
// load so the mismatch is visible without a report per agent call.
const reportShortLifetimeOnce = (lifetimeMs: number) => {
  if (
    reportedShortLifetime ||
    lifetimeMs >= AGENT_HANDOFF_MIN_REMAINING_MS + HANDOFF_FLOOR_MARGIN_MS
  ) {
    return;
  }
  reportedShortLifetime = true;
  datadogRum.addError(
    new Error(
      `Access token lifetime ${Math.round(lifetimeMs / 60_000)} min is under the 8 h agent hand-off target`
    )
  );
};

// Pulls the token from auth-client's in-memory session rather than gating on the `login_method`
// cookie. A long-running agent hand-off needs more headroom than an ordinary API call, so the
// floor is 8 h - 5 min, capped at the token's own lifetime - 5 min: a shorter-lived token would
// otherwise be refreshed on every call. During a refresh outage a cached token with at least
// min(AGENT_STALE_MIN_MS, lifetime / 2) left is still used so runs can start; the lifetime cap
// keeps the fallback reachable for tokens shorter than AGENT_STALE_MIN_MS plus the floor margin.
// Exported for other live-streamed callers outside this module's own AG-UI/AgentCore flow that
// need this same session-token pattern rather than fetch-utils.ts's shorter-horizon one.
export const getAccessToken = async (forceRefresh = false): Promise<string> => {
  // The lifetime is unknown until a token is in hand, so this first call keeps the full threshold.
  const current = await getTokens({ forceRefresh, minStaleMs: AGENT_STALE_MIN_MS });
  const lifetimeMs = tokenLifetimeMs(current);
  if (lifetimeMs === null) return current.accessToken;
  reportShortLifetimeOnce(lifetimeMs);
  if (forceRefresh) return current.accessToken;

  const floorMs = Math.min(
    AGENT_HANDOFF_MIN_REMAINING_MS,
    Math.max(lifetimeMs - HANDOFF_FLOOR_MARGIN_MS, 0)
  );
  if (current.expiresAt - Date.now() >= floorMs) return current.accessToken;
  const { accessToken } = await getTokens({
    minRemainingMs: floorMs,
    minStaleMs: Math.min(AGENT_STALE_MIN_MS, lifetimeMs / 2)
  });
  return accessToken;
};

// Cancel is best-effort and must work while the token service is degraded, so it takes the default
// token rather than the hand-off floor.
const getCancelToken = async (): Promise<string> => (await getTokens()).accessToken;

// Agents embed download markers in this format:
//   <!--artifact:{"filename":"plan.xlsx","url":"https://presigned-s3-url"}-->
// The canonical delivery path is the exact tool result (see artifactFromToolResult
// / onToolCallResultEvent below): the URL is a ~1.5KB presigned S3 link the model
// cannot reproduce as text without corrupting its signature, so we take it
// byte-for-byte from the tool result rather than from chat text. Scanning
// assistant text remains as a fallback for agents that only emit markers inline.
const ARTIFACT_RE = /<!--artifact:\s*(\{[\s\S]*?\})\s*-->/gi;

// Parse the JSON body of an artifact marker into a typed segment, or null when
// it isn't a well-formed {filename, url} object.
const parseArtifactJson = (json: string): AgentArtifactSegment | null => {
  try {
    const parsed: unknown = JSON.parse(json);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as { filename?: unknown }).filename === 'string' &&
      typeof (parsed as { url?: unknown }).url === 'string'
    ) {
      return {
        type: 'artifact',
        filename: (parsed as { filename: string }).filename,
        url: (parsed as { url: string }).url
      };
    }
  } catch {
    // malformed JSON — not an artifact
  }
  return null;
};

// Extract an artifact from a TOOL_CALL_RESULT event's content. The backend
// JSON-encodes the tool's return value, so a string return arrives double-encoded;
// recover the inner string, then scan it for the marker. This is the exact,
// un-mangled copy of the presigned URL.
const artifactFromToolResult = (content: unknown): AgentArtifactSegment | null => {
  if (typeof content !== 'string' || !content) return null;
  let text = content;
  try {
    const decoded: unknown = JSON.parse(content);
    if (typeof decoded === 'string') {
      text = decoded;
    }
  } catch {
    // content wasn't JSON-encoded — scan it directly
  }
  // Fresh non-global regex so we don't share lastIndex state with ARTIFACT_RE.
  const match = new RegExp(ARTIFACT_RE.source, 'i').exec(text);
  return match ? parseArtifactJson(match[1]) : null;
};

const isQueryConfig = (value: unknown): value is QueryConfig =>
  typeof value === 'object' && value !== null && 'queries' in value && Array.isArray(value.queries);

// Reads a field from a TOOL_CALL_RESULT's content, which is the JSON-encoded tool return (possibly
// twice, for a string return), or a Strands-style `{content: [{text}]}` wrapper around that JSON.
// `pick` returns the field from a decoded object, or undefined.
const fromToolResult = <T>(
  content: unknown,
  pick: (decoded: object) => T | undefined,
  depth = 0
): T | undefined => {
  if (depth > 6) return undefined;
  if (typeof content === 'string') {
    try {
      return fromToolResult(JSON.parse(content), pick, depth + 1);
    } catch {
      return undefined;
    }
  }
  if (Array.isArray(content)) {
    return content
      .map(part => fromToolResult(part?.text, pick, depth + 1))
      .find(value => value !== undefined);
  }
  if (typeof content !== 'object' || content === null) return undefined;
  return (
    pick(content) ??
    ('content' in content ? fromToolResult(content.content, pick, depth + 1) : undefined)
  );
};

// The `executed_config` a query tool reports. Undefined for anything else, e.g. an error result.
export const executedConfigFromToolResult = (content: unknown): QueryConfig | undefined =>
  fromToolResult(content, decoded =>
    'executed_config' in decoded && isQueryConfig(decoded.executed_config)
      ? decoded.executed_config
      : undefined
  );

// The `result_id` a query tool reports alongside its executed_config.
export const resultIdFromToolResult = (content: unknown): string | undefined =>
  fromToolResult(content, decoded =>
    'result_id' in decoded && typeof decoded.result_id === 'string' ? decoded.result_id : undefined
  );

// A TOOL_CALL_RESULT's content with up to three layers of JSON encoding removed; undefined when a
// layer does not parse.
const decodeToolResult = (content: unknown): unknown => {
  let decoded = content;
  for (let i = 0; i < 3 && typeof decoded === 'string'; i++) {
    try {
      decoded = JSON.parse(decoded);
    } catch {
      return undefined;
    }
  }
  return decoded;
};

// Whether a TOOL_CALL_RESULT reports success: it carries an executed_config, or its (possibly
// JSON-encoded) top-level `status` is 'success'. Errors, plain text and unparseable content are not.
export const toolResultSucceeded = (content: unknown): boolean => {
  if (executedConfigFromToolResult(content)) return true;
  const decoded = decodeToolResult(content);
  return (
    typeof decoded === 'object' &&
    decoded !== null &&
    'status' in decoded &&
    decoded.status === 'success'
  );
};

// Whether a TOOL_CALL_RESULT explicitly rejects the call: a `status` of 'error' or `valid: false`.
// Plain text, unparseable or missing content is not a rejection.
export const toolResultRejected = (content: unknown): boolean => {
  const decoded = decodeToolResult(content);
  if (typeof decoded !== 'object' || decoded === null) return false;
  return (
    ('status' in decoded && decoded.status === 'error') ||
    ('valid' in decoded && decoded.valid === false)
  );
};

// The tool-call segment with that toolCallId, updated with its result; undefined when the id names
// no tool-call segment. withToolResultContent reads these fields off the result's content.
export const withToolResult = (
  segments: AgentResponseSegment[],
  idToIndex: Map<string, number>,
  toolCallId: string,
  resultOk: boolean,
  config?: QueryConfig,
  resultId?: string
): AgentToolCallSegment | undefined => {
  const idx = idToIndex.get(toolCallId);
  const segment = idx === undefined ? undefined : segments[idx];
  if (segment?.type !== 'tool_call') return undefined;
  return {
    ...segment,
    resultOk,
    ...(config && { executedConfig: config }),
    ...(resultId && { resultId })
  };
};

// withToolResult with every field read off a TOOL_CALL_RESULT's content, as the stream does.
export const withToolResultContent = (
  segments: AgentResponseSegment[],
  idToIndex: Map<string, number>,
  toolCallId: string,
  content: unknown
): AgentToolCallSegment | undefined => {
  const resultOk = toolResultSucceeded(content);
  const updated = withToolResult(
    segments,
    idToIndex,
    toolCallId,
    resultOk,
    executedConfigFromToolResult(content),
    resultOk ? resultIdFromToolResult(content) : undefined
  );
  return updated && toolResultRejected(content) ? { ...updated, resultRejected: true } : updated;
};

// The agent sends a custom event naming the display title of each tool call right after it starts.
export const TOOL_CALL_TITLE_EVENT = 'ToolCallTitle';

export const toolCallTitleFromEvent = (
  name: string,
  value: unknown
): { toolCallId: string; title: string } | undefined => {
  if (name !== TOOL_CALL_TITLE_EVENT || typeof value !== 'object' || value === null) {
    return undefined;
  }
  const { toolCallId, title } = value as { toolCallId?: unknown; title?: unknown };
  if (typeof toolCallId !== 'string' || typeof title !== 'string' || !title.trim()) {
    return undefined;
  }
  return { toolCallId, title: title.trim() };
};

// Records the title of a tool call. The title event arrives right after the call starts, before the
// call's segment is added at its end, so a title for a call not yet in `idToIndex` is held in
// `pending` (see takePendingToolTitle). Returns the updated segment for a call already added.
export const withToolTitle = (
  segments: AgentResponseSegment[],
  idToIndex: Map<string, number>,
  pending: Map<string, string>,
  toolCallId: string,
  title: string
): AgentToolCallSegment | undefined => {
  const idx = idToIndex.get(toolCallId);
  if (idx === undefined) {
    pending.set(toolCallId, title);
    return undefined;
  }
  const segment = segments[idx];
  return segment.type === 'tool_call' ? { ...segment, title } : undefined;
};

export const takePendingToolTitle = (
  pending: Map<string, string>,
  toolCallId: string
): string | undefined => {
  const title = pending.get(toolCallId);
  pending.delete(toolCallId);
  return title;
};

// Replace artifact markers in text segments with typed artifact segments.
// Markers whose filename was already delivered via a tool result are stripped
// from the text but NOT re-added as a button — the tool-result copy is exact,
// whereas a model-echoed URL may be corrupted.
const extractArtifacts = (
  segs: AgentResponseSegment[],
  deliveredFilenames: Set<string> = new Set()
): AgentResponseSegment[] => {
  const result: AgentResponseSegment[] = [];
  for (const seg of segs) {
    if (seg.type !== 'text') {
      result.push(seg);
      continue;
    }
    const artifacts: AgentArtifactSegment[] = [];
    const cleaned = seg.content
      .replace(ARTIFACT_RE, (match, json: string) => {
        const artifact = parseArtifactJson(json);
        if (!artifact) return match; // leave malformed markers as-is
        if (!deliveredFilenames.has(artifact.filename)) {
          artifacts.push(artifact);
        }
        return ''; // strip the marker text either way
      })
      .trim();
    if (cleaned) {
      result.push({ type: 'text', content: cleaned });
    }
    result.push(...artifacts);
  }
  return result;
};

// The user message's content: the text alone, or — with an attachment — the text (when there is
// any) followed by the file as an inline `document` part.
export const buildUserMessageContent = (
  text: string,
  attachment?: AgentAttachment
): string | InputContent[] => {
  if (!attachment) return text;
  const parts: InputContent[] = text ? [{ type: 'text', text }] : [];
  parts.push({
    type: 'document',
    source: { type: 'data', mimeType: attachment.mimeType, value: attachment.data },
    metadata: { filename: attachment.filename }
  });
  return parts;
};

// The run's messages with every user message cut to its text parts. See AttachmentHttpAgent.
export const withoutAttachmentData = (messages: Message[]): Message[] =>
  messages.map(message =>
    message.role === 'user' && Array.isArray(message.content)
      ? { ...message, content: message.content.filter(part => part.type === 'text') }
      : message
  );

// HttpAgent re-copies its whole message list on every streamed event. By the time `apply` runs, the
// request body has already been built from `input`, so an attachment's bytes would only slow that
// copying down (seconds per few hundred tokens at 20 MB). Keep the local copy to the text.
class AttachmentHttpAgent extends HttpAgent {
  protected apply(...args: Parameters<HttpAgent['apply']>): ReturnType<HttpAgent['apply']> {
    this.messages = withoutAttachmentData(this.messages);
    return super.apply(...args);
  }
}

export interface AgentForwardedProps {
  company?: string;
  kpi?: string;
  timestamp?: string;
  modelType?: string;
  handoff?: AgentHandoff;
}

// The agent reads company server-side to resolve company-specific catalog context (custom
// segments), kpi/timestamp to load the matching model run from S3, and modelType to name the MMM
// product (DTC vs Commerce). Include only the truthy keys so an unset field never overrides the
// agent's own default-resolution path.
export const buildForwardedProps = (selection: AgentForwardedProps): AgentForwardedProps =>
  Object.fromEntries(Object.entries(selection).filter(([, value]) => Boolean(value)));

const isAuthError = (err: unknown): boolean => {
  if (!(err instanceof Error)) return false;
  const msg = err.message.toLowerCase();
  return (
    msg.includes('401') ||
    msg.includes('unauthorized') ||
    msg.includes('unauthenticated') ||
    msg.includes('authentication')
  );
};

const CANCEL_RETRY_DELAY_MS = 500;
const CANCEL_MAX_RETRIES = 2;

// `RunAgentResult.result` is `any` in @ag-ui/client, so narrow it before reading a flag.
const isCancelledResult = (result: unknown): boolean =>
  typeof result === 'object' &&
  result !== null &&
  'cancelled' in result &&
  result.cancelled === true;

const isCancelRequestedResult = (result: unknown): boolean =>
  typeof result === 'object' &&
  result !== null &&
  'cancel_requested' in result &&
  result.cancel_requested === true;

const wait = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

const runPromptOnce = (
  token: string,
  {
    agentEndpoint,
    sessionId,
    prompt,
    onUpdate,
    company,
    kpi,
    timestamp,
    modelType,
    sessionContext,
    focusContext,
    onCustomEvent,
    signal,
    tools,
    handoff,
    attachment
  }: SendPromptToAgentArgs
): Promise<AgentResponse> =>
  new Promise((resolve, reject) => {
    // Generated client-side (rather than left for AgentCore to assign) so a cancel request
    // issued mid-run can name the exact run to stop. See `signal` on SendPromptToAgentArgs.
    const runId = crypto.randomUUID();
    const baseHeaders = (bearer: string) => ({
      Authorization: `Bearer ${bearer}`,
      'X-Amzn-Bedrock-AgentCore-Runtime-Session-Id': sessionId
    });
    const AgentClass = attachment ? AttachmentHttpAgent : HttpAgent;
    const agent = new AgentClass({
      url: agentEndpoint,
      headers: {
        ...baseHeaders(token),
        ...(company ? { 'X-Amzn-Bedrock-AgentCore-Runtime-Custom-Client-Slug': company } : {}),
        ...(kpi ? { 'X-Amzn-Bedrock-AgentCore-Runtime-Custom-Kpi': kpi } : {}),
        ...(timestamp ? { 'X-Amzn-Bedrock-AgentCore-Runtime-Custom-Timestamp': timestamp } : {})
      },
      threadId: sessionId
    });

    // Flipped once the run's own stream ends, so a pending cancel retry gives up.
    let streamOpen = true;
    // Asks the backend to flag `runId` as cancelled. Runs on its own short-lived request and never
    // touches `agent`'s connection. It needs a valid token for the same user, so it fetches a fresh
    // one: a long run can outlive `token`. The backend answers `cancel_requested: false` when it
    // hasn't registered the run yet (Stop landed right after send), so retry a couple of times
    // while the stream is still open. Best-effort: the original run resolves either way.
    const requestCancel = async () => {
      for (let attempt = 0; attempt <= CANCEL_MAX_RETRIES && streamOpen; attempt += 1) {
        if (attempt > 0) {
          await wait(CANCEL_RETRY_DELAY_MS);
        }
        if (!streamOpen) return;
        try {
          const cancelToken = await getCancelToken();
          const { result } = await new HttpAgent({
            url: agentEndpoint,
            headers: baseHeaders(cancelToken),
            threadId: sessionId
          }).runAgent({ forwardedProps: { mode: 'cancel', run_id: runId } });
          if (isCancelRequestedResult(result)) return;
        } catch {
          // Retry below; the run itself is unaffected.
        }
      }
    };
    const onAbort = () => {
      requestCancel();
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    // Fold any preambles into the user turn: session grounding (once-per-session) then the current
    // focus (per-turn), each before the prompt with a delimiter.
    const preamble = [sessionContext, focusContext].filter(Boolean).join('\n\n');
    const userContent = preamble ? `${preamble}\n\n---\n\nUser request:\n${prompt}` : prompt;
    agent.messages = [
      {
        id: crypto.randomUUID(),
        role: 'user',
        content: buildUserMessageContent(userContent, attachment)
      }
    ];

    // Collect every segment the agent emits during this run — interleaved
    // text messages and tool calls in their emission order.
    //
    // Why multiple sources:
    //   - TEXT_MESSAGE_END gives us the canonical content for streamed text.
    //   - MESSAGES_SNAPSHOT can carry assistant text that arrived via a
    //     non-streaming path (some agents emit content only via snapshot).
    //     We upsert by messageId so streamed + snapshot don't double-count.
    //   - TOOL_CALL_END gives us the resolved tool name + arguments for each
    //     tool the agent invoked. The runtime hands these to us already
    //     parsed, so we don't need to assemble them from the args-delta
    //     stream ourselves.
    //
    // `inFlight*` tracks whatever's actively streaming so abort/error paths
    // can still surface the partial reply.
    const segments: AgentResponseSegment[] = [];
    const idToIndex = new Map<string, number>();
    // Filenames delivered via a tool result (exact presigned URL). Used to
    // suppress any duplicate/corrupted button from a model-echoed text marker.
    const deliveredArtifactFilenames = new Set<string>();
    let inFlightId: string | undefined;
    let inFlightBuffer = '';

    const notify = () => onUpdate?.([...segments]);

    const upsertText = (id: string, content: string) => {
      if (!content) return;
      const existingIdx = idToIndex.get(id);
      if (existingIdx !== undefined) {
        segments[existingIdx] = { type: 'text', content };
      } else {
        idToIndex.set(id, segments.length);
        segments.push({ type: 'text', content });
      }
      notify();
    };

    const pendingToolTitles = new Map<string, string>();

    const addToolCall = (id: string, name: string, args: Record<string, unknown>) => {
      if (idToIndex.has(id)) return;
      idToIndex.set(id, segments.length);
      const title = takePendingToolTitle(pendingToolTitles, id);
      segments.push({ type: 'tool_call', name, args, ...(title && { title }) });
      notify();
    };

    const setToolTitle = (id: string, title: string) => {
      const updated = withToolTitle(segments, idToIndex, pendingToolTitles, id, title);
      const idx = idToIndex.get(id);
      if (updated && idx !== undefined) {
        segments[idx] = updated;
        notify();
      }
    };

    const addArtifact = (key: string, artifact: AgentArtifactSegment) => {
      if (idToIndex.has(key)) return;
      idToIndex.set(key, segments.length);
      segments.push(artifact);
      deliveredArtifactFilenames.add(artifact.filename);
      notify();
    };

    // Carry the run selection in forwardedProps — this is the primary channel: it reaches the
    // runtime in the payload (the custom headers above are stripped by AgentCore at its boundary,
    // so they're a legacy/CLI-only fallback).
    const forwardedProps = buildForwardedProps({ company, kpi, timestamp, modelType, handoff });

    agent
      .runAgent(
        {
          runId,
          ...(Object.keys(forwardedProps).length > 0 && { forwardedProps }),
          ...(tools && tools.length > 0 && { tools })
        },
        {
          onTextMessageStartEvent({ event }) {
            inFlightId = event.messageId;
            inFlightBuffer = '';
          },
          onTextMessageContentEvent({ event, textMessageBuffer }) {
            // The runtime hands us the buffer *before* this event's delta is
            // appended, so add it back to stay current. End delivers the full
            // content too, but pushing each delta into the segments list as a
            // live upsert lets consumers stream tokens into the UI instead of
            // waiting for the whole reply.
            inFlightBuffer = textMessageBuffer + event.delta;
            upsertText(event.messageId, inFlightBuffer);
          },
          onTextMessageEndEvent({ event, textMessageBuffer }) {
            upsertText(event.messageId, textMessageBuffer);
            inFlightId = undefined;
            inFlightBuffer = '';
          },
          onToolCallEndEvent({ event, toolCallName, toolCallArgs }) {
            addToolCall(event.toolCallId, toolCallName, toolCallArgs);
          },
          onToolCallResultEvent({ event }) {
            const updated = withToolResultContent(
              segments,
              idToIndex,
              event.toolCallId,
              event.content
            );
            const toolCallIdx = idToIndex.get(event.toolCallId);
            if (updated && toolCallIdx !== undefined) {
              segments[toolCallIdx] = updated;
              notify();
            }
            // Deliver the download button from the exact tool result so the
            // presigned URL is never routed through (and mangled by) the model's
            // text. Keyed by toolCallId so a repeated event can't double-add.
            const artifact = artifactFromToolResult(event.content);
            if (artifact) {
              addArtifact(`artifact-${event.toolCallId}`, artifact);
            }
          },
          onMessagesSnapshotEvent({ event }) {
            for (const msg of event.messages) {
              if (msg.role === 'assistant' && typeof msg.content === 'string') {
                upsertText(msg.id, msg.content);
              }
            }
          },
          onCustomEvent({ event }) {
            // Forward the raw AG-UI CUSTOM event (name + value) to the host, which
            // interprets the names it cares about. The transport stays generic — no
            // app-specific event names are baked in here. (`onCustomEvent` below is
            // the destructured prop, not this handler.)
            const { name, value } = event as { name?: unknown; value?: unknown };
            if (typeof name === 'string') {
              const toolTitle = toolCallTitleFromEvent(name, value);
              if (toolTitle) {
                setToolTitle(toolTitle.toolCallId, toolTitle.title);
              }
              onCustomEvent?.({ name, value });
            }
          },
          onRunErrorEvent({ event }: { event: RunErrorEvent }) {
            reject(new Error(event.message));
          }
        }
      )
      .then(runResult => {
        streamOpen = false;
        signal?.removeEventListener('abort', onAbort);
        if (inFlightId && inFlightBuffer && !idToIndex.has(inFlightId)) {
          upsertText(inFlightId, inFlightBuffer);
        }
        // Replace artifact markers in text segments with typed artifact segments.
        // Notify once more so the UI swaps the raw marker text for download buttons.
        // Pass the filenames already delivered via tool results so an echoed
        // (and possibly corrupted) text marker doesn't produce a duplicate button.
        const processed = extractArtifacts(segments, deliveredArtifactFilenames);
        if (processed.some(s => s.type === 'artifact')) {
          onUpdate?.([...processed]);
        }
        const text = processed
          .filter((s): s is AgentTextSegment => s.type === 'text')
          .map(s => s.content)
          .join('\n\n');
        resolve({ text, segments: processed, cancelled: isCancelledResult(runResult.result) });
      })
      .catch(err => {
        streamOpen = false;
        signal?.removeEventListener('abort', onAbort);
        reject(err);
      });
  });

// Stop pressed before a run started (e.g. during the token fetch or the auth retry): there is
// nothing to cancel on the backend, so resolve as cancelled without sending the run.
const CANCELLED_BEFORE_START: AgentResponse = { text: '', segments: [], cancelled: true };

export const sendPromptToAgent = async (input: SendPromptToAgentArgs): Promise<AgentResponse> => {
  let token = await getAccessToken();
  if (input.signal?.aborted) return CANCELLED_BEFORE_START;
  try {
    return await runPromptOnce(token, input);
  } catch (err) {
    if (!isAuthError(err)) throw err;
    token = await getAccessToken(true);
    if (input.signal?.aborted) return CANCELLED_BEFORE_START;
    return runPromptOnce(token, input);
  }
};
